import clsx from "clsx";
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import CommentIcon from "@ui/components/icon/CommentIcon";
import AnimatedNumber, {
  formatNumber,
} from "@ui/components/meta/AnimatedNumber";
import DuetLink from "@ui/components/navigation/DuetLink";
import { SELECTORS } from "@ui/config";
import { useComponentConfig } from "@ui/context/component-config-context";
import { useNavigationContext } from "@ui/context/navigation-context";
import { useCommentCount } from "@ui/hooks/use-comment-count";
import useDefaults from "@ui/hooks/use-defaults";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import {
  useCommentsConfig,
  useConfigContext,
} from "@voxmedia/duet-context/context/config-context";
import CoralCommentsContext from "@voxmedia/duet-context/context/coral-comments-context";
import CycloneCommentsContext from "@voxmedia/duet-context/context/cyclone-comments-context";
import { useIsCycloneCommentsProvider } from "@voxmedia/duet-context/context/use-comment-counts-context";

import * as styles from "./CommentCount.css";
import CommentDrawerOpenerLink from "./CommentDrawerOpenerLink";

export interface MetaCommentCountBaseConfig {
  hideZeroComments?: boolean;
  showContentCardUnreadCount?: boolean;
}
export interface Props extends MetaCommentCountBaseConfig {
  commentCount?: number | null;
  /** Comment count store key for the active comment provider. */
  countId?: string;
  /** @deprecated Use countId */
  coralId?: string;
  url: string;
  noText?: boolean;
  authorName: string;
  headline: string;
  contentType: string;
  className?: string;
  variant?: keyof typeof styles.container;
  componentContext?: string;
}

// Determine if we need to pluralize the "Comment" label based on the count
function formatCommentsLabel(count: number): string {
  if (count === 1) {
    return "Comment";
  }
  return "Comments";
}

function formatUnreadLabel(
  unreadCount: number,
  count: number,
): string | number {
  if (unreadCount === count) {
    return "All";
  }
  return unreadCount;
}

/**
 * Renders the comment count for the active comment provider (Coral or Cyclone).
 * Both providers fetch counts via bulk registration in the shared comment count store.
 */
const CommentCount: FunctionComponent<Props> = (props) => {
  const { coralConfig } = useConfigContext();
  const { location: commentLocation } = useCommentsConfig();
  const isCycloneCommentsProvider = useIsCycloneCommentsProvider();
  const { onCommentCountClick } = useNavigationContext();
  const analytics = useAnalytics();

  // Track if the CSS animation has already played to prevent restart on rerenders
  const hasAnimatedRef = useRef(false);

  // Mark animation as played after it completes
  useEffect(() => {
    if (!hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        hasAnimatedRef.current = true;
      }, 400); // Match the CSS animation duration (0.4s)
      return () => clearTimeout(timer);
    }
  }, []);

  if (!isCycloneCommentsProvider && !coralConfig) {
    throw new Error("missing coral config");
  }

  const metaCommentCountConfig = useComponentConfig(
    "metaCommentCount",
    props.componentContext || "",
  );

  const {
    commentCount,
    url,
    hideZeroComments,
    showContentCardUnreadCount,
    countId,
    coralId,
    authorName,
    headline,
    contentType,
    className,
    variant = "lede",
  } = useDefaults(props, metaCommentCountConfig);

  const resolvedCountId = countId ?? coralId;
  if (!resolvedCountId) {
    throw new Error("CommentCount requires countId");
  }

  const { count } = useCommentCount(resolvedCountId);
  const cycloneContext = useContext(CycloneCommentsContext);
  const coralContext = useContext(CoralCommentsContext);
  const commentsContext = cycloneContext ?? coralContext;
  if (!commentsContext) {
    throw new Error("CommentCount must be used within CommentProvider");
  }
  const { getLastReadCount } = commentsContext;

  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadCount, setLastReadCount] = useState<number | null>(null);

  useEffect(() => {
    const loadLastReadCount = async () => {
      try {
        const count = await getLastReadCount(resolvedCountId);
        setLastReadCount(count);
      } catch {
        setLastReadCount(0);
      }
    };

    void loadLastReadCount();
  }, [resolvedCountId, getLastReadCount]);

  useEffect(() => {
    if (count === null) {
      return;
    }

    if (!lastReadCount) {
      setUnreadCount(count);
    } else {
      setUnreadCount(Math.max(0, count - lastReadCount));
    }
  }, [count, lastReadCount]);

  // Determine if animation should be enabled based on variant
  const shouldAnimate =
    variant === "lede" ||
    variant === "fullWidthLede" ||
    variant === "splitLede";

  // Drawer links are Coral-only today; cyclone uses inline body embeds.
  const shouldOpenDrawer =
    !isCycloneCommentsProvider &&
    commentLocation === "drawer" &&
    (variant === "lede" ||
      variant === "fullWidthLede" ||
      variant === "splitLede" ||
      variant === "button" ||
      variant === "stickyNav" ||
      variant === "quickPostPermalink" ||
      variant === "contentCard");

  // Render the content once - this will be passed as children to both link components
  const renderContent = () => {
    // Handle null/empty count case
    if (count === null) {
      return <></>;
    }

    switch (variant) {
      case "contentCard":
        return (
          <>
            <CommentIcon
              width={12}
              height={12}
              viewBox="0 0 12 12"
              className={styles.contentCardCommentIcon}
            />
            <span className={styles.contentCardCommentText}>
              {showContentCardUnreadCount ? (
                // FIXME: Remove commentCount after fixing setReadCount for in-5 games
                <>
                  <NumberDisplay number={count} /> {formatCommentsLabel(count)}{" "}
                  {unreadCount > 0 && !commentCount
                    ? ` / ${formatUnreadLabel(unreadCount, count)} New`
                    : ""}
                </>
              ) : (
                <NumberDisplay number={count} />
              )}
            </span>
          </>
        );
      case "button":
        return (
          <span className={styles.linkText}>
            <NumberDisplay number={count} />
            &nbsp;{formatCommentsLabel(count)}
          </span>
        );
      default:
        return (
          <div className={styles.articleCommentContainer}>
            <span className={styles.articleCommentIconContainer}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 32 33"
                className={styles.articleCommentIcon}
              >
                <title>Comments</title>
                <path
                  className={styles.bubble[variant]}
                  d="M2.7 4h23.6c1.209 0 2.2.991 2.2 2.2v16.2c0 1.21-.991 2.2-2.2 2.2H5.193L.5 29.293V6.2l.012-.223A2.21 2.21 0 0 1 2.7 4Z"
                />
              </svg>
              <span className={styles.bubbleCount[variant]}>
                <NumberDisplay number={count} />
              </span>
            </span>
            {!["stickyNav", "quickPostRiver"].includes(variant) && (
              <span className={styles.label}>
                {formatCommentsLabel(count)}
                {unreadCount > 0 &&
                  ` (${formatUnreadLabel(unreadCount, count)} New)`}
              </span>
            )}
          </div>
        );
    }
  };

  // Component to render the number with or without animation
  const NumberDisplay = ({ number }: { number: number }) => {
    if (shouldAnimate) {
      return (
        <AnimatedNumber
          number={number}
          formatted
          ariaLabel={`${formatCommentsLabel(number)}: ${formatNumber(number)}`}
        />
      );
    }
    return <span>{formatNumber(number)}</span>;
  };

  if (
    count === null ||
    (hideZeroComments && count === 0 && variant === "contentCard")
  ) {
    // If there are no comments and we're hiding zero comments, don't render on content cards
    return null;
  }

  return (
    <>
      <span
        className={clsx(
          styles.container[variant],
          variant !== "button" &&
            !hasAnimatedRef.current &&
            styles.animatedContainer,
          className,
        )}
      >
        {shouldOpenDrawer ? (
          <CommentDrawerOpenerLink
            href={url}
            className={clsx(SELECTORS.CommentsLink, styles.link)}
            variant={variant}
            headline={headline}
            authorName={authorName}
          >
            {renderContent()}
          </CommentDrawerOpenerLink>
        ) : (
          <DuetLink
            href={url}
            className={clsx(SELECTORS.CommentsLink, styles.link)}
            onClick={() => {
              // this ensures that the sticky nav is visible (if it should be) when the comment count is clicked
              // and the user is taken to the comment section
              onCommentCountClick();
              // Handle analytics based on variant
              switch (variant) {
                case "quickPostRiver":
                  analytics?.emit({
                    event: "quickpost_click",
                    quickpost_click: {
                      url: url,
                      title: headline,
                      author: authorName,
                      component_name: variant,
                      variant: "comment",
                    },
                  });
                  return;
                default:
                  // General analytics for non-drawer comment counts
                  analytics?.emit({
                    event: "article_click",
                    article_click: {
                      article_uri: url,
                      article_title: headline,
                      author: authorName,
                      page_type: contentType,
                    },
                  });
                  return;
              }
            }}
          >
            {renderContent()}
          </DuetLink>
        )}
      </span>
    </>
  );
};

export default CommentCount;
